import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import api from "../../axiosInstance";

export const fetchCollections = createAsyncThunk(
  "collections/fetchAll",
  async (params = {}, { rejectWithValue }) => {
    try {
      const { data } = await api.get(`/collections`, { params });
      return data;
    } catch (err) {
      return rejectWithValue(
        err.response?.data?.message || "Failed to load collections",
      );
    }
  },
);
export const fetchCollectionBySlug = createAsyncThunk(
  "collections/fetchBySlug",
  async (slug, { rejectWithValue }) => {
    try {
      const { data } = await api.get(`/collections/${slug}`);
      return data;
    } catch (err) {
      return rejectWithValue(
        err.response?.data?.message || "Failed to load collection",
      );
    }
  },
);

// ── Authenticated ──
export const fetchProductsForPicker = createAsyncThunk(
  "collections/fetchProducts",
  async (division, { rejectWithValue }) => {
    try {
      // NOTE: original code called the relative path "/api/products",
      // which resolves against the frontend's own origin, not the
      // backend — almost certainly a bug outside of a dev proxy setup.
      // Pointed at `/product` to match the route used in productActions.js.
      // CONFIRM this matches your actual backend route before shipping.
      const { data } = await api.get(`/product`, {
        params: { division, limit: 200 },
      });
      return data.data || data;
    } catch (err) {
      return rejectWithValue(
        err.response?.data?.message || "Failed to load products",
      );
    }
  },
);

export const createCollection = createAsyncThunk(
  "collections/create",
  async (payload, { rejectWithValue }) => {
    try {
      const { data } = await api.post(`/collections`, payload);
      return data;
    } catch (err) {
      return rejectWithValue(
        err.response?.data?.message || "Failed to create collection",
      );
    }
  },
);

export const updateCollection = createAsyncThunk(
  "collections/update",
  async ({ id, payload }, { rejectWithValue }) => {
    try {
      const { data } = await api.put(`/collections/${id}`, payload);
      return data;
    } catch (err) {
      return rejectWithValue(
        err.response?.data?.message || "Failed to update collection",
      );
    }
  },
);

export const deleteCollection = createAsyncThunk(
  "collections/delete",
  async (id, { rejectWithValue }) => {
    try {
      await api.delete(`/collections/${id}`);
      return id;
    } catch (err) {
      return rejectWithValue(
        err.response?.data?.message || "Failed to delete collection",
      );
    }
  },
);

export const toggleCollectionStatus = createAsyncThunk(
  "collections/toggleStatus",
  async ({ id, status }, { rejectWithValue }) => {
    try {
      const { data } = await api.patch(`/collections/${id}/status`, {
        status,
      });
      return data;
    } catch (err) {
      return rejectWithValue(
        err.response?.data?.message || "Failed to update status",
      );
    }
  },
);
// ---------------------------------------------------------------------------
// Slice
// ---------------------------------------------------------------------------
const initialState = {
  items: [],
  current: null,
  pickerProducts: [],
  meta: null,
  loading: false,
  pickerLoading: false,
  actionLoading: false,
  error: null,
  actionError: null,
};

const collectionsSlice = createSlice({
  name: "collections",
  initialState,
  reducers: {
    clearCurrent: (state) => {
      state.current = null;
      state.actionError = null;
    },
    clearError: (state) => {
      state.error = null;
      state.actionError = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // fetch all
      .addCase(fetchCollections.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchCollections.fulfilled, (state, action) => {
        state.loading = false;
        state.items = action.payload.data;
        state.meta = action.payload.meta;
      })
      .addCase(fetchCollections.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // fetch by slug
      .addCase(fetchCollectionBySlug.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchCollectionBySlug.fulfilled, (state, action) => {
        state.loading = false;
        state.current = action.payload.data;
      })
      .addCase(fetchCollectionBySlug.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // fetch products for picker
      .addCase(fetchProductsForPicker.pending, (state) => {
        state.pickerLoading = true;
      })
      .addCase(fetchProductsForPicker.fulfilled, (state, action) => {
        state.pickerLoading = false;
        state.pickerProducts = action.payload;
      })
      .addCase(fetchProductsForPicker.rejected, (state, action) => {
        state.pickerLoading = false;
        state.actionError = action.payload;
      })

      // create
      .addCase(createCollection.pending, (state) => {
        state.actionLoading = true;
        state.actionError = null;
      })
      .addCase(createCollection.fulfilled, (state, action) => {
        state.actionLoading = false;
        state.items.unshift(action.payload.data);
      })
      .addCase(createCollection.rejected, (state, action) => {
        state.actionLoading = false;
        state.actionError = action.payload;
      })

      // update
      .addCase(updateCollection.pending, (state) => {
        state.actionLoading = true;
        state.actionError = null;
      })
      .addCase(updateCollection.fulfilled, (state, action) => {
        state.actionLoading = false;
        const updated = action.payload.data;
        const idx = state.items.findIndex(
          (i) => (i._id || i.id) === updated._id,
        );
        if (idx !== -1) state.items[idx] = updated;
        if (state.current?._id === updated._id) state.current = updated;
      })
      .addCase(updateCollection.rejected, (state, action) => {
        state.actionLoading = false;
        state.actionError = action.payload;
      })

      // delete
      .addCase(deleteCollection.pending, (state) => {
        state.actionLoading = true;
        state.actionError = null;
      })
      .addCase(deleteCollection.fulfilled, (state, action) => {
        state.actionLoading = false;
        state.items = state.items.filter(
          (i) => (i._id || i.id) !== action.payload,
        );
      })
      .addCase(deleteCollection.rejected, (state, action) => {
        state.actionLoading = false;
        state.actionError = action.payload;
      })

      // toggle status
      .addCase(toggleCollectionStatus.pending, (state) => {
        state.actionLoading = true;
        state.actionError = null;
      })
      .addCase(toggleCollectionStatus.fulfilled, (state, action) => {
        state.actionLoading = false;
        const updated = action.payload.data;
        const idx = state.items.findIndex(
          (i) => (i._id || i.id) === updated._id,
        );
        if (idx !== -1) state.items[idx] = updated;
      })
      .addCase(toggleCollectionStatus.rejected, (state, action) => {
        state.actionLoading = false;
        state.actionError = action.payload;
      });
  },
});

export const { clearCurrent, clearError } = collectionsSlice.actions;
export default collectionsSlice.reducer;
